import { Button, Checkbox, FormControlLabel, Typography } from "@mui/material";
import NextAction from "../../utils/NextAction";

interface ContrlProps {
  showDistance: boolean;
  setShowDistance: React.Dispatch<React.SetStateAction<boolean>>;
  restart: () => void;
  discretize: () => void;
  discretizeBoundary: () => void;
  draw: () => void;
  resumePolygons: () => void;
  removeVerticesInPolygons: () => void;
  removeVerticesOnBoundaries: () => void;
}

export default function Controls({
  showDistance,
  setShowDistance,
  restart,
  discretize,
  discretizeBoundary,
  draw,
  resumePolygons,
  removeVerticesInPolygons,
  removeVerticesOnBoundaries,
}: ContrlProps) {
  return (
    <div className="controls">
      <div>
        <Typography variant="h5" style={{ paddingBottom: 20 }}>
          Generalized Voronoi Diagram
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Here, we assume you already understand what a Voronoi Diagram is. In
          reality, the obstacles are not points; they are polygons. The right
          side shows an example of a Generalized Voronoi Diagram in a real
          environment with some obstacle polygons. How can we draw this
          Generalized Voronoi Diagram with polygons? Let me show you step by
          step.
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Here is a restart button that allows you to reset at any time. Click
          this button to start the tutorial.
        </Typography>
        <Button variant={"contained"} sx={{ mb: 4 }} onClick={restart}>
          Start
        </Button>
        <Typography variant="h6" style={{ paddingBottom: 10 }}>
          1. Discretize the Polygons
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          At first, the only information we know is the position of each
          obstacle. Because the polygons are continuous, we first discretize the
          edges of the polygons into several points. Each point can be a site in
          the Voronoi Diagram, which we do not want to encounter. Click the
          button to see the result.
        </Typography>
        <Button variant={"contained"} sx={{ mb: 4 }} onClick={discretize}>
          Discretize
        </Button>
        <Typography variant="h6" style={{ paddingBottom: 10 }}>
          2. Discretize the Boundaries
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          We also need to discretize the boundary because we do not want the
          robot to encounter it while moving.
        </Typography>
        <Button
          variant={"contained"}
          sx={{ mb: 4 }}
          onClick={discretizeBoundary}
        >
          Discretize the Boundaries
        </Button>
        <Typography variant="h6" style={{ paddingBottom: 10 }}>
          3. Draw the Voronoi Digram
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Now, we have several sites, and we can create the Voronoi Diagram.
          Draw the Voronoi Diagram using Fortune's Algorithm.
        </Typography>
        <Button variant={"contained"} sx={{ mb: 4 }} onClick={draw}>
          Generate Voronoi Diagram
        </Button>
        <Typography variant="h6" style={{ paddingBottom: 10 }}>
          4. Add Polygons back
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          After drawing the Voronoi diagram, we see many edges and vertices.
          There are some edges and vertices we do not want. We move the polygon
          obstacles back to help us observe.
        </Typography>
        <Button variant={"contained"} sx={{ mb: 4 }} onClick={resumePolygons}>
          Resume Polygons
        </Button>
        <Typography variant="h6" style={{ paddingBottom: 10 }}>
          5. Remove Voronoi Edges inside the Polygons
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          After resuming the obstacles, we find that the vertices inside the
          polygons are the ones we don't want. We remove the vertices and edges
          inside the polygons.
        </Typography>
        <Button
          variant={"contained"}
          sx={{ mb: 4 }}
          onClick={removeVerticesInPolygons}
        >
          Remove vertices in polygons
        </Button>
        <Typography variant="h6" style={{ paddingBottom: 10 }}>
          6. Remove Voronoi Edges connected to the Boundaries
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Then, we find that the edges connected to the boundaries are also ones
          we don't want. Remove them!
        </Typography>
        <Button
          variant={"contained"}
          sx={{ mb: 4 }}
          onClick={removeVerticesOnBoundaries}
        >
          Remove vertices on boundaries
        </Button>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Congratulations! Now you have the <b>Generalized Voronoi Diagram</b>{" "}
          of a real-world environment with polygon obstacles.
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Next, we will show how to do motion planning based on the{" "}
          <b>Generalized Voronoi Diagram</b> with a start point and a target
          point.
        </Typography>
        <NextAction
          label={"Next: Path Planning with Voronoi Diagram"}
          to="/robotics/path-planning-with-voronoi"
        />
      </div>
    </div>
  );
}
